// @ts-nocheck
/*
 * Port of views/dashboard/voice-plan/list.js (origin/main-dev, 808 lines).
 * BYTE-IDENTICAL — custom vp-list-table (NOT obd3-gui DataTable — the
 * page has its own visual design with vp-plan-name-cell/CallTypeChips/
 * Pulse Details + Price Details buttons/status toggle), 4 modals
 * (details/pulse/price/delete), demo-plan section (isSuperAdmin only),
 * marquee flash-message, `hasPermission` read-guard, `Voice Plans`
 * ACL literal preserved. PrimeReact Button in delete modal preserved.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { getApiCall, postApiCall, fetchAndSetFlashMessage } from '@/service/httpCall';
import { Card as ShadCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
// Card compound shim: preserves Card.Header/Card.Body compound-usage sites.
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ className, children, ...props }: any) => (
  <h5 className={_cx('card-title', className)} {...props}>
    {children}
  </h5>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Modal: any = ({
  show,
  onHide,
  size,
  centered: _c,
  children,
  className,
  dialogClassName,
  ...props
}: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
        ? 'sm:max-w-2xl'
        : size === 'xl'
          ? 'sm:max-w-4xl'
          : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${dialogClassName || ''} ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { API_VOICE_PLAN_LIST, API_VOICE_PLAN_DELETE } from '@/service/url';
import { ROUTES, USER_ROLES, MODULE_ACL, FLASH_MESSAGE_ACTION_OBJECT } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
const VOICE_PLAN_BLOCKED_ROLES = [
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
  USER_ROLES.CALL_CENTRE_AGENT,
  USER_ROLES.CALL_CENTRE,
];
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { getUser } from '@/utilities/setting';
import { formatVoicePlanId, getPlansGroupedByCallType } from '@/utilities/voicePlanUtils';
import { CallTypeChip, CallTypeIcon, PlanStatusBadge } from '@/features/voice-plan/voice-plan-shared';
import VoicePlanDetailsPanel from '@/features/voice-plan/VoicePlanDetailsPanel';
import {
  VoicePlanPulseDetailsContent,
  VoicePlanPriceDetailsContent,
  VoicePlanMetricDialogHeader,
} from '@/features/voice-plan/VoicePlanMetricModals';
import { FaWaveSquare, FaSearch, FaEye, FaPlus, FaTrashAlt, FaRupeeSign } from 'react-icons/fa';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download } from 'lucide-react';
import '@/features/voice-plan/voice-plan-pages.scss';
import { FillPage } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const VoicePlanList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [statusList, setStatusList] = useState({});
  const [voicePlan, setVoicePlan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [originalDataList, setOriginalDataList] = useState([]);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [pulseModalVisible, setPulseModalVisible] = useState(false);
  const [priceModalVisible, setPriceModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState({});
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const openFullDetails = (row) => {
    setSelectedPlanDetails(row);
    setDetailsModalVisible(true);
  };

  const openPulseDetails = (row) => {
    setSelectedPlanDetails(row);
    setPulseModalVisible(true);
  };

  const openPriceDetails = (row) => {
    setSelectedPlanDetails(row);
    setPriceModalVisible(true);
  };

  const closeMetricModals = () => {
    setDetailsModalVisible(false);
    setPulseModalVisible(false);
    setPriceModalVisible(false);
    setSelectedPlanDetails(null);
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'voice-plans-list',
        data: voicePlan,
        columns: [
          { header: 'Plan ID', accessor: (row) => formatVoicePlanId(row.id) },
          { header: 'Plan Name', accessor: (row) => row.plan_name || '-' },
          { header: 'Created By', accessor: (row) => row.user?.username || '-' },
          {
            header: 'Created Date',
            accessor: (row) =>
              row.req_date
                ? new Date(row.req_date).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                  })
                : '-',
          },
          {
            header: 'Plan Status',
            accessor: (row) => ((statusList[row.id] ?? row.plan_status == 1) ? 'Enabled' : 'Disabled'),
          },
          { header: 'Demo', accessor: (row) => (row.is_demo ? 'Yes' : 'No') },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  useEffect(() => {
    checkAuth();
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  const fetchVoicePlans = async () => {
    setLoading(true);
    try {
      getApiCall(API_VOICE_PLAN_LIST)
        .then(onVoicePlanSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    } catch (error) {
      console.error('Error fetching voice plans:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
    fetchVoicePlans();
    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.VOICE_PLANS &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.LIST &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  useEffect(() => {
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function onVoicePlanSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      val.key = idx + 1;
      return val;
    });
    setVoicePlan(updatedList);
    setOriginalDataList(updatedList);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    navigate(`/${ROUTES.VOICE_PLAN.CREATE}`);
  };

  const confirmDelete = (id) => {
    setPlanToDelete(id);
    setDeleteModalVisible(true);
  };

  const executeDelete = async () => {
    try {
      const url = `${API_VOICE_PLAN_DELETE}/${planToDelete}`;
      const response = await postApiCall(url);
      if (response) {
        toast.success(response.message || 'Voice Plan deleted successfully');
        await fetchVoicePlans();
      } else {
        toast.error(response?.message || 'Error deleting voice plan');
      }
    } catch (error) {
      let message = 'Error deleting voice plan';
      if (error.response?.data?.message?.toLowerCase?.().includes('allocated')) {
        message = 'Cannot delete this voice plan as it is currently allocated to users.';
      } else {
        message = friendlyApiError(error) || message;
      }
      toast.error(message);
    } finally {
      setDeleteModalVisible(false);
      setPlanToDelete(null);
    }
  };

  const handleStatusToggle = async (planId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await postApiCall(`/v2/voice-plan/${planId}`, { status: newStatus ? 1 : 0 });
      setStatusList((prev) => ({ ...prev, [planId]: newStatus }));
      if (!newStatus) {
        setVoicePlan((prev) => prev.filter((plan) => plan.id !== planId));
        setOriginalDataList((prev) => prev.filter((plan) => plan.id !== planId));
      } else {
        await fetchVoicePlans();
      }
      toast.success(`Plan status updated to ${newStatus ? 'Enabled' : 'Disabled'}`);
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Failed to update plan status');
    }
  };

  const isPlanEnabled = (row) => statusList[row.id] ?? row.plan_status == 1;

  const filteredPlans = useMemo(() => {
    let data = [...originalDataList];

    if (statusFilter === 'enabled') {
      data = data.filter((row) => isPlanEnabled(row));
    } else if (statusFilter === 'disabled') {
      data = data.filter((row) => !isPlanEnabled(row));
    }

    data.sort((a, b) => {
      const dateA = new Date(a.req_date).getTime();
      const dateB = new Date(b.req_date).getTime();
      return dateB - dateA;
    });

    return data;
  }, [originalDataList, statusFilter, statusList]);

  const isCallCentreScopedRole = VOICE_PLAN_BLOCKED_ROLES.includes(getUser()?.role);
  const canRead =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.VOICE_PLANS, 'read');
  const canAdd =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.VOICE_PLANS, 'add');
  const canEdit =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.VOICE_PLANS, 'edit');
  const canDelete =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.VOICE_PLANS, 'delete');

  if (!canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px' }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const fmtCreatedDate = (v: any) =>
    v
      ? new Date(v).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'plan_name',
      header: 'Plan Name',
      cell: ({ row }) => (
        <div>
          <div className="text-sm font-semibold text-foreground">
            {(() => {
              const n = row.original.plan_name || '';
              return n.length > 24 ? `${n.slice(0, 22)}…` : n;
            })()}
          </div>
          <div className="text-[11px] text-muted-foreground">
            ID: {formatVoicePlanId(row.original.id)}
          </div>
        </div>
      ),
    },
    {
      id: 'call_types',
      header: 'Call Types',
      enableSorting: false,
      cell: ({ row }) => {
        const groups = getPlansGroupedByCallType(row.original.plans);
        return (
          <div className="vp-ct-chips">
            {groups.map((g) => (
              <span
                key={g.type}
                title={g.type.charAt(0) + g.type.slice(1).toLowerCase()}
              >
                <CallTypeIcon type={g.type} size={14} />
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: 'pulse',
      header: 'Pulse',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => openPulseDetails(row.original)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
          title="View pulse details"
          aria-label="View pulse details"
        >
          <FaWaveSquare className="h-3.5 w-3.5" />
        </button>
      ),
    },
    {
      id: 'price',
      header: 'Price',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => openPriceDetails(row.original)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
          title="View price details"
          aria-label="View price details"
        >
          <FaRupeeSign className="h-3.5 w-3.5" />
        </button>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row: any) => (isPlanEnabled(row) ? 'ENABLED' : 'DISABLED'),
      cell: ({ row }) => <PlanStatusBadge enabled={isPlanEnabled(row.original)} />,
    },
    {
      accessorKey: 'is_demo',
      header: 'Demo',
      cell: ({ row }) =>
        row.original.is_demo ? (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
            Yes
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">No</span>
        ),
    },
    {
      id: 'created_by',
      header: 'Created By',
      accessorFn: (row: any) => row.user?.username || '',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.user?.username || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'req_date',
      header: 'Created Date',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {fmtCreatedDate(row.original.req_date)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 140,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const enabled = isPlanEnabled(row.original);
        const canDeleteRow =
          canDelete &&
          canDelete &&
          row.original.is_allocated == 0;
        return (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openFullDetails(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
              title="View complete details"
              aria-label="View complete details"
            >
              <FaEye className="h-3.5 w-3.5" />
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => handleStatusToggle(row.original.id, enabled)}
                aria-pressed={enabled}
                aria-label={enabled ? 'Disable voice plan' : 'Enable voice plan'}
                title={enabled ? 'Disable voice plan' : 'Enable voice plan'}
                className={
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ring-1 ring-inset ' +
                  (enabled
                    ? 'bg-emerald-500 ring-emerald-600'
                    : 'bg-muted/70 ring-border/70')
                }
              >
                <span
                  className={
                    'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
                    (enabled ? 'translate-x-[18px]' : 'translate-x-[2px]')
                  }
                />
              </button>
            )}
            {canDeleteRow && (
              <button
                type="button"
                onClick={() => confirmDelete(row.original.id)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete voice plan"
                aria-label="Delete voice plan"
              >
                <FaTrashAlt className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <FillPage>
    <div className="vp-list-page">

      <Row>
        <Col sm="12">
          <Card>
            <Card.Body>
              <div className="vp-list-header">
                <div className="vp-list-header__left">
                  <div className="vp-list-header__icon">
                    <FaWaveSquare />
                  </div>
                  <div>
                    <h4 className="vp-list-header__title">Voice Plans</h4>
                    <p className="vp-list-header__subtitle">
                      View and manage all voice plans created in the system.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter by status"
                    className="input-embossed h-9 w-[150px] rounded-md px-2.5 text-xs"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {voicePlan.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExport}
                      disabled={buttonDisable}
                      className="gap-1.5"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export
                      {buttonDisable && (
                        <span
                          className="ml-1 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                          aria-hidden
                        />
                      )}
                    </Button>
                  )}
                  {canAdd && (
                    <button
                      type="button"
                      onClick={handleCreateButtonClick}
                      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-primary bg-primary px-4 text-xs font-bold text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 cursor-pointer"
                    >
                      <FaPlus />
                      Create Voice Plan
                    </button>
                  )}
                </div>
              </div>

              {isSuperAdmin && (() => {
                const demoPlan = voicePlan.find((p) => p.is_demo);
                return (
                  <div
                    className="vp-demo-section"
                    style={{ borderRadius: '8px', padding: '25px', marginBottom: '16px' }}
                  >
                    <h5 style={{ marginBottom: '12px' }}>Demo Voice Plan</h5>
                    {demoPlan ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                        <div>
                          <b>Plan Name:</b> {demoPlan.plan_name}
                        </div>
                        <div>
                          <b>ID:</b> {formatVoicePlanId(demoPlan.id)}
                        </div>
                        <div>
                          <b>Status:</b> {isPlanEnabled(demoPlan) ? 'Enabled' : 'Disabled'}
                        </div>
                        <div>
                          <b>Created By:</b> {demoPlan.user?.username || '—'}
                        </div>
                        <div className="vp-ct-chips">
                          {getPlansGroupedByCallType(demoPlan.plans).map((g) => (
                            <CallTypeChip key={g.type} type={g.type} />
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm vp-table-action-btn"
                          onClick={() => openFullDetails(demoPlan)}
                        >
                          View Details
                        </button>
                      </div>
                    ) : (
                      <div style={{ color: '#6c757d' }}>No demo voice plan configured yet.</div>
                    )}
                  </div>
                );
              })()}

              <DataTable
                tableId="voice-plans:list:v1"
                columns={columns}
                data={filteredPlans}
                searchPlaceholder="Search by plan name…"
                emptyTitle="No voice plans found"
                emptyDescription="Create a voice plan to configure inbound, outbound, and missed-call pricing."
                getRowId={(row: any) => String(row.id)}
                initialColumnPinning={{ right: ['actions'] }}
                pageSize={rowsPerPage}
                disableExport
              />

              {/* Legacy custom table below has been replaced by the shared
                  DataTable above; the removed markup is kept out of the tree. */}
              {false && (
              <div className="vp-list-table-wrap">
                <table className="vp-list-table">
                  <thead>
                    <tr>
                      <th>Plan Name</th>
                      <th>Call Types</th>
                      <th>Pulse Details</th>
                      <th>Price Details</th>
                      <th>Status</th>
                      <th>Demo</th>
                      <th>Created By</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPlans.length > 0 ? (
                      paginatedPlans.map((row) => {
                        const groups = getPlansGroupedByCallType(row.plans);
                        const enabled = isPlanEnabled(row);
                        return (
                          <tr key={row.id}>
                            <td>
                              <div className="vp-plan-name-cell">
                                <div className="vp-plan-name-cell__title">
                                  {(() => {
                                    const n = row.plan_name;
                                    return n.length > 10 ? `${n.slice(0, 8)}...` : n;
                                  })()}
                                </div>
                                <div className="vp-plan-name-cell__id">
                                  ID: {formatVoicePlanId(row.id)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="vp-ct-chips">
                                {groups.map((g) => (
                                  <span
                                    key={g.type}
                                    title={g.type.charAt(0) + g.type.slice(1).toLowerCase()}
                                  >
                                    <CallTypeIcon type={g.type} size={14} />
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => openPulseDetails(row)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
                                title="View pulse details"
                                aria-label="View pulse details"
                              >
                                <FaWaveSquare className="h-3.5 w-3.5" />
                              </button>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => openPriceDetails(row)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
                                title="View price details"
                                aria-label="View price details"
                              >
                                <FaRupeeSign className="h-3.5 w-3.5" />
                              </button>
                            </td>
                            <td>
                              <PlanStatusBadge enabled={enabled} />
                            </td>
                            <td>{row.is_demo ? 'Yes' : 'No'}</td>
                            <td>{row.user?.username || '—'}</td>
                            <td>
                              {row.req_date
                                ? new Date(row.req_date).toLocaleString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                                  })
                                : '—'}
                            </td>
                            <td>
                              <div className="vp-actions-cell">
                                <button
                                  type="button"
                                  className="vp-row-action"
                                  title="View complete details"
                                  onClick={() => openFullDetails(row)}
                                >
                                  <FaEye />
                                </button>
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => handleStatusToggle(row.id, enabled)}
                                    aria-pressed={enabled}
                                    aria-label={enabled ? 'Disable voice plan' : 'Enable voice plan'}
                                    title={enabled ? 'Disable voice plan' : 'Enable voice plan'}
                                    className={
                                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ring-1 ring-inset ' +
                                      (enabled
                                        ? 'bg-emerald-500 ring-emerald-600'
                                        : 'bg-muted/70 ring-border/70')
                                    }
                                  >
                                    <span
                                      className={
                                        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
                                        (enabled ? 'translate-x-[18px]' : 'translate-x-[2px]')
                                      }
                                    />
                                  </button>
                                )}
                                {canDelete &&
                                  canDelete &&
                                  row.is_allocated == 0 && (
                                    <button
                                      type="button"
                                      className="vp-row-action vp-row-action--danger"
                                      title="Delete voice plan"
                                      onClick={() => confirmDelete(row.id)}
                                    >
                                      <FaTrashAlt />
                                    </button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9} className="vp-list-empty">
                          No voice plans found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}

              {false && filteredPlans.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-1 pb-2 pt-3 text-xs text-muted-foreground">
                  <span>
                    Showing{' '}
                    <span className="font-medium text-foreground">{showingFrom}</span>
                    –
                    <span className="font-medium text-foreground">{showingTo}</span>{' '}
                    of{' '}
                    <span className="font-medium text-foreground">
                      {filteredPlans.length}
                    </span>
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 text-xs font-semibold text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Previous
                    </button>
                    <span className="whitespace-nowrap tabular-nums">
                      Page{' '}
                      <span className="font-medium text-foreground">{currentPage}</span>
                      {' / '}
                      <span className="font-medium text-foreground">
                        {Math.max(totalPages, 1)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 text-xs font-semibold text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      Next
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                    <div className="ml-2 flex items-center gap-2">
                      <span>Rows:</span>
                      <select
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        className="input-embossed h-8 rounded-md px-2 text-xs"
                      >
                        {[10, 25, 50].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={pulseModalVisible}
        dialogClassName="vp-details-dialog vp-details-dialog--metric"
        onHide={closeMetricModals}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <VoicePlanMetricDialogHeader variant="pulse" planName={selectedPlanDetails?.plan_name} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlanDetails && <VoicePlanPulseDetailsContent plan={selectedPlanDetails} />}
        </Modal.Body>
      </Modal>

      <Modal
        show={priceModalVisible}
        dialogClassName="vp-details-dialog vp-details-dialog--metric"
        onHide={closeMetricModals}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <VoicePlanMetricDialogHeader variant="price" planName={selectedPlanDetails?.plan_name} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlanDetails && <VoicePlanPriceDetailsContent plan={selectedPlanDetails} />}
        </Modal.Body>
      </Modal>

      <Modal
        show={detailsModalVisible}
        dialogClassName="vp-details-dialog"
        onHide={closeMetricModals}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Voice Plan Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlanDetails && (
            <VoicePlanDetailsPanel
              plan={selectedPlanDetails}
              enabled={isPlanEnabled(selectedPlanDetails)}
              canEdit={canEdit}
              compact
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={deleteModalVisible} onHide={() => setDeleteModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this voice plan?</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex gap-2">
            <Button variant="outline" onClick={() => setDeleteModalVisible(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={executeDelete}>
              Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
    </FillPage>
  );
};

export default VoicePlanList;
